import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { downloadCsv, fileStamp, slugForFile } from '../../lib/download';
import { formatDate, formatNumber } from '../../lib/format';
import { useHeldResource, useResource } from '../../lib/hooks';
import type { AmClientRow, Product } from '../../lib/types';
import cmitLogo from '../../assets/cmit-logo.png';
import { StateBadge } from './amUi';

/**
 * Account Management › Business case.
 *
 * The spreadsheet a business case used to be built in, as a page: what the
 * client pays today beside what we propose, and what the difference is worth.
 *
 * - TODAY starts as every service on the client's active Autotask contracts
 *   (the Services tab's read), each ticked in or left out. Anything else they
 *   pay for — the Microsoft licences bought elsewhere, the backup somebody
 *   else bills — is added beside them from the catalogue or typed in.
 * - PROPOSED is built from the catalogue, typed-in lines, or today's lines
 *   carried across, plus the one-time cost of getting there (migration,
 *   hardware, labour).
 * - The band at the top compares the two: monthly, yearly, the difference,
 *   the one-time investment and how many months the saving takes to pay it
 *   back.
 *
 * Saved against the client (coo.AmBusinessCase, migration 192). A contract
 * line is kept as Autotask had it on the day, so a case presented in March
 * still says what it said in March. Nothing is written to Autotask. The
 * printable document under the editor carries the included lines only.
 */

type Side = 'today' | 'proposed';
type Period = 'Monthly' | 'Quarterly' | 'Semi-Annual' | 'Yearly' | 'One-time';
const PERIODS: Period[] = ['Monthly', 'Quarterly', 'Semi-Annual', 'Yearly', 'One-time'];
const STATUSES = ['Draft', 'Presented', 'Accepted', 'Declined'] as const;
type Status = (typeof STATUSES)[number];

interface Line {
  key: string;
  side: Side;
  source: 'contract' | 'catalogue' | 'custom';
  included: boolean;
  group: string;
  name: string;
  vendor?: string | null;
  period: Period;
  units: number;
  price: number;
  contract?: string | null;
  contractServiceId?: number | null;
  productId?: number | null;
}

interface CaseSummary {
  id: string;
  clientId: string;
  clientName: string;
  title: string;
  recommendation: string | null;
  notes: string | null;
  status: Status;
  monthlyToday: number;
  monthlyProposed: number;
  oneTime: number;
  lineCount: number;
  updatedBy: string | null;
  updatedUtc: string;
  lines?: Line[];
}

interface ContractService {
  id: number;
  contractId: number;
  contract: string;
  service: string;
  period: string | null;
  units: number | null;
  unitPrice: number | null;
}
interface ServicesPayload {
  linked: boolean;
  companyName: string | null;
  contracts: { id: number; name: string }[];
  services: ContractService[];
}

const money = (v: number) => formatNumber(v, 'currency');
/* Line figures carry cents: a $317.40 licence is not a $317 one, and the
   totals under it would not add up to what is shown. */
const cents = (v: number) => v.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const newKey = () => `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

/** How much of a billing period falls in a month; one-time is not recurring.
 *  The renewals workbench's rule, and the API's. */
const PER_MONTH: Record<Period, number> = { Monthly: 1, Quarterly: 1 / 3, 'Semi-Annual': 1 / 6, Yearly: 1 / 12, 'One-time': 0 };

/** Autotask's billing-period label, as one of ours. */
function periodOf(label: string | null | undefined): Period {
  const p = (label ?? 'Monthly').toLowerCase();
  if (/semi/.test(p)) return 'Semi-Annual';
  if (/quarter/.test(p)) return 'Quarterly';
  if (/year|annual/.test(p)) return 'Yearly';
  if (/one|once/.test(p)) return 'One-time';
  return 'Monthly';
}

const monthlyOf = (l: Line) => l.units * l.price * PER_MONTH[l.period];
const oneTimeOf = (l: Line) => (l.period === 'One-time' ? l.units * l.price : 0);

/** The case's figures — included lines only. A one-time line on the today
 *  side is history, so it is in neither run rate nor the investment. */
function totalsOf(lines: Line[]) {
  let today = 0;
  let proposed = 0;
  let oneTime = 0;
  for (const l of lines) {
    if (!l.included) continue;
    if (l.side === 'today') today += monthlyOf(l);
    else {
      proposed += monthlyOf(l);
      oneTime += oneTimeOf(l);
    }
  }
  const saving = today - proposed;
  return {
    today,
    proposed,
    oneTime,
    saving,
    /* Months for the saving to cover the one-time cost. Only meaningful when
       there is both a saving and something to pay back. */
    payback: saving > 0 && oneTime > 0 ? oneTime / saving : null,
    /* Three years of each, the one-time cost on the proposed side. */
    threeYear: saving * 36 - oneTime,
  };
}

/** Lines grouped under their heading, in the order the headings first appear. */
function grouped(lines: Line[]) {
  const groups = new Map<string, Line[]>();
  for (const l of lines) {
    const g = l.group.trim() || 'Other';
    groups.set(g, [...(groups.get(g) ?? []), l]);
  }
  return [...groups];
}

function contractLines(services: ContractService[]): Line[] {
  return services.map((s) => ({
    key: newKey(),
    side: 'today',
    source: 'contract',
    included: true,
    group: s.contract || 'Contract',
    name: s.service,
    period: periodOf(s.period),
    units: s.units ?? 1,
    price: s.unitPrice ?? 0,
    contract: s.contract,
    contractServiceId: s.id,
  }));
}

/* =========================================================================
   The list
   ========================================================================= */

export function AmBusinessCases({ organizationName }: { organizationName: string }) {
  const clients = useResource<AmClientRow[]>('/am/clients');
  const [clientFilter, setClientFilter] = useState('');
  const list = useResource<CaseSummary[]>(`/am/business-cases${clientFilter ? `?clientId=${encodeURIComponent(clientFilter)}` : ''}`);
  const [opened, setOpened] = useState<{ id: string | null; clientId: string; clientName: string; title?: string } | null>(null);
  const [starting, setStarting] = useState(false);

  if (opened) {
    const close = () => {
      setOpened(null);
      list.reload();
    };
    return opened.id ? (
      <CaseLoader id={opened.id} organizationName={organizationName} onClose={close} />
    ) : (
      <CaseEditor
        initial={null}
        clientId={opened.clientId}
        clientName={opened.clientName}
        startTitle={opened.title}
        organizationName={organizationName}
        onClose={close}
        onSaved={(saved) => setOpened({ id: saved.id, clientId: saved.clientId, clientName: saved.clientName })}
      />
    );
  }

  const rows = list.data ?? [];
  return (
    <div className="mt-4">
      <section className="card">
        <div className="card-head">
          <div className="grow">
            <h3>Business cases</h3>
            <p className="sub">
              What a client pays today beside what we propose — from their Autotask contracts, the catalogue and lines typed in — with the
              saving, the one-time cost and the payback.
            </p>
          </div>
          <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}>
            <Field label="Client">
              <select className="select" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
                <option value="">All clients</option>
                {(clients.data ?? []).map((c) => (
                  <option key={c.AmClientId} value={c.AmClientId}>
                    {c.Name}
                  </option>
                ))}
              </select>
            </Field>
            <button type="button" className="btn btn-primary" onClick={() => setStarting(true)}>
              New business case
            </button>
          </div>
        </div>
        {list.error && <ErrorBanner message={list.error} />}
        {list.loading && !list.data ? (
          <Loading label="Loading business cases" />
        ) : rows.length === 0 ? (
          <div className="card-body">
            <Empty title="No business cases yet">
              <p>Start one for a client: their contract services come in on their own, and you choose what stays.</p>
            </Empty>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Business case</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th className="num">Today /mo</th>
                  <th className="num">Proposed /mo</th>
                  <th className="num">Difference /mo</th>
                  <th className="num">One-time</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const diff = r.monthlyToday - r.monthlyProposed;
                  return (
                    <tr key={r.id}>
                      <td>
                        <button
                          type="button"
                          className="link-button strong"
                          onClick={() => setOpened({ id: r.id, clientId: r.clientId, clientName: r.clientName })}
                        >
                          {r.title}
                        </button>
                      </td>
                      <td>{r.clientName}</td>
                      <td>
                        <StateBadge status={r.status} />
                      </td>
                      <td className="num">{money(r.monthlyToday)}</td>
                      <td className="num">{money(r.monthlyProposed)}</td>
                      <td className={`num strong ${diff >= 0 ? 'bc-saving' : 'bc-increase'}`}>
                        {diff >= 0 ? '' : '+'}
                        {money(Math.abs(diff))} {diff >= 0 ? 'saved' : 'more'}
                      </td>
                      <td className="num">{money(r.oneTime)}</td>
                      <td className="xs muted">
                        {formatDate(r.updatedUtc)}
                        {r.updatedBy ? ` · ${r.updatedBy}` : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {starting && (
        <StartCase
          clients={clients.data ?? []}
          initialClientId={clientFilter}
          onCancel={() => setStarting(false)}
          onStart={(clientId, title) => {
            const client = (clients.data ?? []).find((c) => c.AmClientId === clientId);
            setStarting(false);
            setOpened({ id: null, clientId, clientName: client?.Name ?? 'Client', title });
          }}
        />
      )}
    </div>
  );
}

function StartCase({
  clients,
  initialClientId,
  onCancel,
  onStart,
}: {
  clients: AmClientRow[];
  initialClientId: string;
  onCancel: () => void;
  onStart: (clientId: string, title: string) => void;
}) {
  const [clientId, setClientId] = useState(initialClientId);
  const [title, setTitle] = useState('Business case');
  return (
    <Modal
      title="New business case"
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={!clientId || !title.trim()} onClick={() => onStart(clientId, title.trim())}>
            Start
          </button>
        </>
      }
    >
      <Field label="Client" hint="Their active Autotask contracts are read in as today's services.">
        <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)} autoFocus>
          <option value="">Choose a client…</option>
          {clients.map((c) => (
            <option key={c.AmClientId} value={c.AmClientId}>
              {c.Name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Title">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
    </Modal>
  );
}

function CaseLoader({ id, organizationName, onClose }: { id: string; organizationName: string; onClose: () => void }) {
  const c = useResource<CaseSummary>(`/am/business-cases/${id}`);
  if (c.error) return <ErrorBanner message={c.error} />;
  if (!c.data) return <Loading label="Opening the business case" />;
  return (
    <CaseEditor
      key={c.data.id}
      initial={c.data}
      clientId={c.data.clientId}
      clientName={c.data.clientName}
      organizationName={organizationName}
      onClose={onClose}
      onSaved={() => c.reload()}
    />
  );
}

/* =========================================================================
   The editor
   ========================================================================= */

function CaseEditor({
  initial,
  clientId,
  clientName,
  startTitle,
  organizationName,
  onClose,
  onSaved,
}: {
  initial: CaseSummary | null;
  clientId: string;
  clientName: string;
  startTitle?: string;
  organizationName: string;
  onClose: () => void;
  onSaved: (saved: CaseSummary) => void;
}) {
  const services = useHeldResource<ServicesPayload>(`/am/clients/${clientId}/autotask/services`);
  const [title, setTitle] = useState(initial?.title ?? startTitle ?? 'Business case');
  const [recommendation, setRecommendation] = useState(initial?.recommendation ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [status, setStatus] = useState<Status>(initial?.status ?? 'Draft');
  const [lines, setLines] = useState<Line[]>(initial?.lines ?? []);
  /* A new case takes the contracts once, when they arrive. A saved one keeps
     the snapshot it was saved with; "Pull from contracts" adds what is new. */
  const [seeded, setSeeded] = useState(initial !== null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const snapshot = (t = title, r = recommendation, n = notes, s = status, l = lines) => JSON.stringify([t, r, n, s, l]);
  const [savedAs, setSavedAs] = useState<string | null>(initial ? snapshot() : null);
  const dirty = savedAs !== snapshot();

  useEffect(() => {
    if (seeded || !services.data) return;
    if (services.data.linked) setLines((ls) => [...contractLines(services.data!.services), ...ls]);
    setSeeded(true);
  }, [seeded, services.data]);

  const totals = useMemo(() => totalsOf(lines), [lines]);
  const today = lines.filter((l) => l.side === 'today');
  const proposed = lines.filter((l) => l.side === 'proposed');

  const update = (key: string, patch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const remove = (key: string) => setLines((ls) => ls.filter((l) => l.key !== key));
  const add = (line: Omit<Line, 'key'>) => setLines((ls) => [...ls, { ...line, key: newKey() }]);
  const setGroupIncluded = (side: Side, group: string, included: boolean) =>
    setLines((ls) => ls.map((l) => (l.side === side && (l.group.trim() || 'Other') === group ? { ...l, included } : l)));
  const carry = (l: Line) => add({ ...l, side: 'proposed', included: true });
  const carryAll = () => {
    const included = today.filter((l) => l.included);
    setLines((ls) => [...ls, ...included.map((l) => ({ ...l, key: newKey(), side: 'proposed' as const }))]);
  };

  /* Adds the contract services not already on the case, unticked when the
     case already has lines — a service that appeared since is news, not a
     decision somebody has made. */
  const pullContracts = () => {
    if (!services.data?.linked) return;
    const have = new Set(lines.map((l) => l.contractServiceId).filter((v) => v !== null && v !== undefined));
    const fresh = contractLines(services.data.services.filter((s) => !have.has(s.id)));
    setLines((ls) => [...ls, ...fresh.map((l) => ({ ...l, included: today.length === 0 }))]);
    setNotice(fresh.length === 0 ? 'Every contract service is already on the case.' : `${fresh.length} contract service${fresh.length === 1 ? '' : 's'} added.`);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = { title: title.trim() || 'Business case', recommendation, notes, status, lines };
      const saved = initial
        ? await api.put<CaseSummary>(`/am/business-cases/${initial.id}`, payload)
        : await api.post<CaseSummary>('/am/business-cases', { ...payload, clientId });
      setSavedAs(snapshot());
      setNotice('Saved.');
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the business case');
    } finally {
      setBusy(false);
    }
  };

  const back = () => {
    if (dirty && lines.length > 0 && !window.confirm('Leave without saving? Your changes to this business case will be lost.')) return;
    onClose();
  };

  const exportCsv = () =>
    downloadCsv(`${slugForFile(clientName)}_business_case_${fileStamp()}.csv`, [
      ['Side', 'Included', 'Group', 'Line', 'Source', 'Period', 'Units', 'Unit price', 'Monthly', 'Yearly', 'One-time'],
      ...lines.map((l) => [
        l.side === 'today' ? 'Today' : 'Proposed',
        l.included ? 'Yes' : 'No',
        l.group,
        l.name,
        l.source,
        l.period,
        l.units,
        l.price,
        monthlyOf(l).toFixed(2),
        (monthlyOf(l) * 12).toFixed(2),
        oneTimeOf(l).toFixed(2),
      ]),
    ]);

  return (
    <div className="mt-4">
      <div className="no-print">
        <div className="row-between wrap mb-4">
          <button type="button" className="btn btn-ghost btn-sm" onClick={back}>
            ← All business cases
          </button>
          <div className="row wrap" style={{ gap: 'var(--space-2)' }}>
            {initial && (
              <DeleteButton
                what={`the business case "${initial.title}"`}
                onConfirm={async () => {
                  await api.del(`/am/business-cases/${initial.id}`);
                  onClose();
                }}
              />
            )}
            <button type="button" className="btn btn-secondary btn-sm" onClick={exportCsv} disabled={lines.length === 0}>
              Export CSV
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => window.print()}>
              Print / save PDF
            </button>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || (!dirty && initial !== null)} onClick={save}>
              {busy ? 'Saving…' : initial ? (dirty ? 'Save changes' : 'Saved') : 'Save business case'}
            </button>
          </div>
        </div>

        {error && <ErrorBanner message={error} />}

        <section className="card mb-4">
          <div className="card-body bc-details">
            <Field label="Title">
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            <Field label="Client">
              <input className="input" value={clientName} readOnly />
            </Field>
            <Field label="Status">
              <select className="select" value={status} onChange={(e) => setStatus(e.target.value as Status)}>
                {STATUSES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
            <div className="bc-wide">
              <Field label="Recommendation" hint="The case in a sentence or two. Printed under the title.">
                <textarea
                  className="textarea"
                  rows={2}
                  value={recommendation}
                  placeholder="e.g. Put all files in one secure, manageable container, with Microsoft security policy management enforcing the security policies."
                  onChange={(e) => setRecommendation(e.target.value)}
                />
              </Field>
            </div>
          </div>
        </section>

        <SummaryBand totals={totals} />

        {notice && <p className="xs muted mt-2">{notice}</p>}

        {/* ---------- today ---------- */}
        <section className="card mt-4">
          <div className="card-head">
            <div className="grow">
              <h3>Today</h3>
              <p className="sub">
                What {clientName} pays now. Contract services are read from Autotask — untick what should not be in the comparison — and anything
                they pay for elsewhere is added below.
              </p>
            </div>
            <div className="row wrap" style={{ gap: 'var(--space-2)' }}>
              <button type="button" className="btn btn-ghost btn-sm" disabled={services.refreshing} onClick={services.refresh}>
                {services.refreshing ? 'Reading Autotask…' : 'Re-read Autotask'}
              </button>
              <button type="button" className="btn btn-secondary btn-sm" disabled={!services.data?.linked} onClick={pullContracts}>
                Pull from contracts
              </button>
            </div>
          </div>
          {services.loading && !services.data && <Loading label="Reading the client's contracts from Autotask" />}
          {services.error && <ErrorBanner message={`Autotask: ${services.error}`} />}
          {services.data && !services.data.linked && (
            <div className="card-body">
              <p className="small muted">This client has no Autotask company linked, so there are no contract lines to bring in. Add today's services below.</p>
            </div>
          )}
          <LineTable
            side="today"
            lines={today}
            onUpdate={update}
            onRemove={remove}
            onGroupIncluded={setGroupIncluded}
            onCarry={carry}
          />
          <AddLine side="today" onAdd={add} />
        </section>

        {/* ---------- proposed ---------- */}
        <section className="card mt-4">
          <div className="card-head">
            <div className="grow">
              <h3>Proposed</h3>
              <p className="sub">
                What we recommend instead. Carry a line across from today, add from the catalogue, or type a custom line. One-time costs —
                migration, hardware, labour — go here too.
              </p>
            </div>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!today.some((l) => l.included)} onClick={carryAll}>
              Copy today's included lines
            </button>
          </div>
          <LineTable side="proposed" lines={proposed} onUpdate={update} onRemove={remove} onGroupIncluded={setGroupIncluded} />
          <AddLine side="proposed" onAdd={add} />
        </section>

        <section className="card mt-4 mb-4">
          <div className="card-body">
            <Field label="Internal notes" hint="For the team. Not printed.">
              <textarea className="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </div>
        </section>

        <h3 className="mt-6 mb-2">The document</h3>
        <p className="xs muted mb-2">What prints, on one landscape page: the included lines only, without the recommendation or the internal notes. A long case is scaled down to fit.</p>
      </div>

      <CaseDocument
        organizationName={organizationName}
        clientName={clientName}
        title={title}
        lines={lines}
        totals={totals}
      />
    </div>
  );
}

function SummaryBand({ totals }: { totals: ReturnType<typeof totalsOf> }) {
  const saving = totals.saving >= 0;
  return (
    <div className="am-hero">
      <div className="am-hero-stat">
        <div className="label">Today</div>
        <div className="figure">{money(totals.today)}</div>
        <div className="foot">a month · {money(totals.today * 12)} a year</div>
      </div>
      <div className="am-hero-stat">
        <div className="label">Proposed</div>
        <div className="figure">{money(totals.proposed)}</div>
        <div className="foot">a month · {money(totals.proposed * 12)} a year</div>
      </div>
      <div className="am-hero-stat">
        <div className="label">{saving ? 'Saving' : 'Increase'}</div>
        <div className={`figure${saving ? '' : ' flagged'}`}>{money(Math.abs(totals.saving))}</div>
        <div className="foot">a month · {money(Math.abs(totals.saving) * 12)} a year</div>
      </div>
      <div className="am-hero-stat">
        <div className="label">One-time investment</div>
        <div className="figure">{money(totals.oneTime)}</div>
        <div className="foot">
          {totals.payback !== null
            ? `pays back in ${totals.payback.toFixed(1)} months`
            : totals.oneTime > 0
              ? 'no monthly saving to pay it back'
              : 'nothing up front'}
        </div>
      </div>
      <div className="am-hero-stat">
        <div className="label">Over three years</div>
        <div className={`figure${totals.threeYear >= 0 ? '' : ' flagged'}`}>{money(Math.abs(totals.threeYear))}</div>
        <div className="foot">{totals.threeYear >= 0 ? 'saved' : 'more'}, after the one-time cost</div>
      </div>
    </div>
  );
}

/* =========================================================================
   The lines
   ========================================================================= */

function LineTable({
  side,
  lines,
  onUpdate,
  onRemove,
  onGroupIncluded,
  onCarry,
}: {
  side: Side;
  lines: Line[];
  onUpdate: (key: string, patch: Partial<Line>) => void;
  onRemove: (key: string) => void;
  onGroupIncluded: (side: Side, group: string, included: boolean) => void;
  onCarry?: (line: Line) => void;
}) {
  if (lines.length === 0) {
    return (
      <div className="card-body">
        <p className="small muted">{side === 'today' ? 'No lines for today yet.' : 'Nothing proposed yet.'}</p>
      </div>
    );
  }
  const groupNames = [...new Set(lines.map((l) => l.group.trim()).filter(Boolean))];
  const listId = `bc-groups-${side}`;
  return (
    <div className="table-wrap">
      <datalist id={listId}>
        {groupNames.map((g) => (
          <option key={g} value={g} />
        ))}
      </datalist>
      <table className="table bc-table">
        <thead>
          <tr>
            <th style={{ width: 32 }} aria-label="Included" />
            <th>Line</th>
            <th>Group</th>
            <th>Bills</th>
            <th className="num">Units</th>
            <th className="num">Unit price</th>
            <th className="num">Monthly</th>
            <th className="num">Yearly</th>
            <th className="num">One-time</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {grouped(lines).map(([group, rows]) => {
            const all = rows.every((l) => l.included);
            const some = rows.some((l) => l.included);
            const sub = rows.filter((l) => l.included);
            const subMonthly = sub.reduce((n, l) => n + monthlyOf(l), 0);
            const subOneTime = sub.reduce((n, l) => n + oneTimeOf(l), 0);
            const recurring = rows.some((l) => l.period !== 'One-time');
            return (
              <Fragment key={group}>
                <tr className="bc-group">
                  <td>
                    <input
                      type="checkbox"
                      checked={all}
                      ref={(el) => {
                        if (el) el.indeterminate = some && !all;
                      }}
                      aria-label={`Include everything in ${group}`}
                      onChange={(e) => onGroupIncluded(side, group, e.target.checked)}
                    />
                  </td>
                  <td colSpan={5} className="strong">
                    {group}
                  </td>
                  <td className="num strong">{recurring ? cents(subMonthly) : ''}</td>
                  <td className="num strong">{recurring ? cents(subMonthly * 12) : ''}</td>
                  <td className="num strong">{subOneTime ? cents(subOneTime) : ''}</td>
                  <td />
                </tr>
                {rows.map((l) => (
                  <LineRow key={l.key} line={l} listId={listId} onUpdate={onUpdate} onRemove={onRemove} onCarry={onCarry} />
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LineRow({
  line: l,
  listId,
  onUpdate,
  onRemove,
  onCarry,
}: {
  line: Line;
  listId: string;
  onUpdate: (key: string, patch: Partial<Line>) => void;
  onRemove: (key: string) => void;
  onCarry?: (line: Line) => void;
}) {
  /* Today's contract lines are Autotask's: the name is theirs, and dropping
     one is an untick, so the evidence stays on the case. */
  const fromContract = l.side === 'today' && l.source === 'contract';
  const monthly = monthlyOf(l);
  return (
    <tr className={l.included ? undefined : 'bc-excluded'}>
      <td>
        <input type="checkbox" checked={l.included} aria-label={`Include ${l.name}`} onChange={(e) => onUpdate(l.key, { included: e.target.checked })} />
      </td>
      <td>
        {fromContract ? (
          <>
            <div className="small">{l.name}</div>
            <div className="xs subtle">From the contract</div>
          </>
        ) : (
          <>
            <input className="input input-sm" value={l.name} placeholder="What it is" aria-label="Line" onChange={(e) => onUpdate(l.key, { name: e.target.value })} />
            <div className="xs subtle">
              {l.source === 'catalogue' ? `Catalogue${l.vendor ? ` · ${l.vendor}` : ''}` : l.source === 'contract' ? 'Carried from the contract' : 'Custom line'}
            </div>
          </>
        )}
      </td>
      <td>
        <input
          className="input input-sm"
          style={{ minWidth: 120 }}
          list={listId}
          value={l.group}
          aria-label="Group"
          onChange={(e) => onUpdate(l.key, { group: e.target.value })}
        />
      </td>
      <td>
        <select className="select input-sm" value={l.period} aria-label="Bills" onChange={(e) => onUpdate(l.key, { period: e.target.value as Period })}>
          {PERIODS.map((p) => (
            <option key={p}>{p}</option>
          ))}
        </select>
      </td>
      <td className="num">
        <input
          className="input input-sm num"
          style={{ width: 72 }}
          type="number"
          min="0"
          step="1"
          value={l.units}
          aria-label="Units"
          onChange={(e) => onUpdate(l.key, { units: Math.max(0, Number(e.target.value) || 0) })}
        />
      </td>
      <td className="num">
        <input
          className="input input-sm num"
          style={{ width: 96 }}
          type="number"
          step="0.01"
          value={l.price}
          aria-label="Unit price"
          onChange={(e) => onUpdate(l.key, { price: Number(e.target.value) || 0 })}
        />
      </td>
      <td className="num">{l.period === 'One-time' ? '—' : cents(monthly)}</td>
      <td className="num">{l.period === 'One-time' ? '—' : cents(monthly * 12)}</td>
      <td className="num">{l.period === 'One-time' ? cents(oneTimeOf(l)) : '—'}</td>
      <td className="bc-actions">
        {onCarry && (
          <button type="button" className="btn btn-ghost btn-sm" title="Copy this line to Proposed" onClick={() => onCarry(l)}>
            → Proposed
          </button>
        )}
        {!fromContract && (
          <button type="button" className="btn btn-ghost btn-icon btn-sm" title={`Remove ${l.name || 'this line'}`} onClick={() => onRemove(l.key)}>
            ✕
          </button>
        )}
      </td>
    </tr>
  );
}

/** Add a line: from the catalogue, typed in, or (proposed only) a one-time cost. */
function AddLine({ side, onAdd }: { side: Side; onAdd: (line: Omit<Line, 'key'>) => void }) {
  const [searching, setSearching] = useState(false);
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(term.trim()), 220);
    return () => clearTimeout(t);
  }, [term]);
  const results = useResource<Product[]>(searching && q.length >= 2 ? `/products?q=${encodeURIComponent(q)}` : null);

  const custom = (oneTime: boolean) =>
    onAdd({
      side,
      source: 'custom',
      included: true,
      group: oneTime ? 'One-time costs' : side === 'today' ? 'Other services today' : 'Proposed services',
      name: '',
      period: oneTime ? 'One-time' : 'Monthly',
      units: 1,
      price: 0,
    });

  return (
    <div className="card-body bc-add">
      {searching ? (
        <div className="am-product-search">
          <input
            className="input"
            value={term}
            autoFocus
            placeholder="Search the catalogue — name, description or vendor"
            aria-label="Search the product catalogue"
            onChange={(e) => setTerm(e.target.value)}
          />
          {q.length >= 2 && (
            <div className="am-product-results">
              {results.loading && <p className="xs subtle">Searching…</p>}
              {!results.loading && (results.data ?? []).length === 0 && <p className="xs subtle">Nothing in the catalogue matches "{q}".</p>}
              {(results.data ?? []).slice(0, 12).map((p) => (
                <button
                  key={p.ProductId}
                  type="button"
                  className="am-product-result"
                  onClick={() => {
                    onAdd({
                      side,
                      source: 'catalogue',
                      included: true,
                      group: p.Category ?? (side === 'today' ? 'Other services today' : 'Proposed services'),
                      name: p.Name,
                      vendor: p.Vendor,
                      period: p.PeriodType,
                      units: 1,
                      price: p.UnitPrice,
                      productId: p.ProductId,
                    });
                    setTerm('');
                  }}
                >
                  <span className="grow">
                    <span className="small">{p.Name}</span>
                    <span className="xs subtle">{[p.Vendor, p.Category].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span className="mono xs">
                    {money(p.UnitPrice)}
                    {p.PeriodType === 'One-time' ? '' : p.PeriodType === 'Yearly' ? '/yr' : '/mo'}
                  </span>
                </button>
              ))}
            </div>
          )}
          <button type="button" className="btn btn-ghost btn-sm mt-2" onClick={() => setSearching(false)}>
            Done
          </button>
        </div>
      ) : (
        <div className="row wrap" style={{ gap: 'var(--space-2)' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSearching(true)}>
            + Add from the catalogue
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => custom(false)}>
            + Add a custom line
          </button>
          {side === 'proposed' && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => custom(true)}>
              + Add a one-time cost
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   The printable document
   ========================================================================= */

/* The page the document prints on — letter, landscape, 0.4in margins (the
   bc-sheet @page rule in app.css). In CSS pixels, at 96 to the inch. */
const SHEET_WIDTH = 10.2 * 96;
const SHEET_HEIGHT = 7.7 * 96;

/**
 * A business case is handed over as ONE landscape page, as the spreadsheet
 * was. The layout is built to fit — the comparison beside the recommendation,
 * today beside proposed — and a case with more lines than that holds is
 * scaled down to the page just before printing, rather than running onto a
 * second sheet.
 *
 * Scaled, not just shrunk: a smaller scale is laid out wider (the sheet's
 * width over the scale), so it still fills the page from side to side, and the
 * wider columns wrap fewer service names — which makes it shorter, which lets
 * the scale come back up a little. Measured with the
 * styles that print, so the scale is worked out from the layout the printer
 * gets.
 */
export function useFitToSheet() {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const before = () => {
      const el = ref.current;
      if (!el) return;
      /* As it prints: without the card's frame. */
      el.style.removeProperty('--bc-fit');
      el.style.padding = '0';
      el.style.border = '0';
      const heightAt = (fit: number) => {
        el.style.width = `${SHEET_WIDTH / fit}px`;
        return el.scrollHeight;
      };
      /* Under the page by a margin: the print engine sets the same layout
         about 1% taller than the screen does, and one row over is a second
         sheet. */
      const page = SHEET_HEIGHT * 0.96;
      /* The largest scale whose layout fits, found by halving: a wider
         layout is never taller, so halving closes in on it. */
      let fit = 1;
      if (heightAt(1) > page) {
        let lo = 0.2;
        let hi = 1;
        for (let i = 0; i < 10; i++) {
          const mid = (lo + hi) / 2;
          if (heightAt(mid) * mid <= page) lo = mid;
          else hi = mid;
        }
        fit = lo;
      }
      el.style.width = el.style.padding = el.style.border = '';
      el.style.setProperty('--bc-fit', fit.toFixed(4));
    };
    const after = () => ref.current?.style.removeProperty('--bc-fit');
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
    };
  }, []);
  return ref;
}

/** The return on the one-time investment, as the years the monthly saving
 *  takes to make it back. */
function PaybackYears({ totals }: { totals: ReturnType<typeof totalsOf> }) {
  return (
    <div>
      <div className="am-doc-title">Return on investment</div>
      {totals.payback !== null ? (
        <>
          <div className="bc-doc-roi">{(totals.payback / 12).toFixed(1)} years</div>
          <p>
            to make back the {money(totals.oneTime)} one-time investment from the {money(totals.saving)} a month saving
            ({totals.payback.toFixed(1)} months).
          </p>
        </>
      ) : (
        /* No monthly saving to make it back with: shown as nothing, not as a negative. */
        <div className="bc-doc-roi">0 years</div>
      )}
    </div>
  );
}

function CaseDocument({
  organizationName,
  clientName,
  title,
  lines,
  totals,
}: {
  organizationName: string;
  clientName: string;
  title: string;
  lines: Line[];
  totals: ReturnType<typeof totalsOf>;
}) {
  const ref = useFitToSheet();
  const included = lines.filter((l) => l.included);
  const side = (s: Side) => included.filter((l) => l.side === s);
  const saving = totals.saving >= 0;
  return (
    <article className="am-doc bc-doc" ref={ref}>
      <header className="am-doc-head">
        <div>
          <img className="am-doc-logo" src={cmitLogo} alt="CMIT Solutions" />
          <div className="am-doc-brand">{organizationName}</div>
          <h2>{title || 'Business case'}</h2>
        </div>
        <div className="am-doc-meta">
          <div>
            <strong>Prepared for:</strong> {clientName}
          </div>
          <div>
            <strong>Date:</strong> {formatDate(new Date().toISOString())}
          </div>
        </div>
      </header>

      <div className={`bc-doc-top${totals.oneTime > 0 ? '' : ' bc-doc-top-solo'}`}>
        <div>
          <div className="am-doc-title">The comparison</div>
          <table className="am-doc-table">
            <thead>
              <tr>
                <th />
                <th className="num">Monthly</th>
                <th className="num">Yearly</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Today</td>
                <td className="num">{cents(totals.today)}</td>
                <td className="num">{cents(totals.today * 12)}</td>
              </tr>
              <tr>
                <td>Proposed</td>
                <td className="num">{cents(totals.proposed)}</td>
                <td className="num">{cents(totals.proposed * 12)}</td>
              </tr>
              <tr>
                <td>
                  <strong>{saving ? 'Saving' : 'Increase'}</strong>
                </td>
                <td className="num">
                  <strong>{cents(Math.abs(totals.saving))}</strong>
                </td>
                <td className="num">
                  <strong>{cents(Math.abs(totals.saving) * 12)}</strong>
                </td>
              </tr>
              {totals.oneTime > 0 && (
                <tr>
                  <td>One-time investment</td>
                  <td className="num" colSpan={2}>
                    {money(totals.oneTime)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {totals.oneTime > 0 && <PaybackYears totals={totals} />}
      </div>

      <div className="bc-doc-sides">
        {(['today', 'proposed'] as Side[]).map((s) => {
          const rows = side(s);
          /* Only a side with a one-time line gets the column; on today's side
             that is rare, and the width is worth more to the service names. */
          const oneTimeColumn = rows.some((l) => l.period === 'One-time');
          const cols = oneTimeColumn ? 6 : 5;
          return (
            <div key={s}>
              <div className="am-doc-title">{s === 'today' ? 'Today' : 'Proposed'}</div>
              <table className="am-doc-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th className="num">Units</th>
                    <th className="num">Unit price</th>
                    <th className="num">Monthly</th>
                    <th className="num">Yearly</th>
                    {oneTimeColumn && <th className="num">One-time</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={cols} className="muted">
                        Nothing included.
                      </td>
                    </tr>
                  )}
                  {grouped(rows).map(([group, ls]) => {
                    const m = ls.reduce((n, l) => n + monthlyOf(l), 0);
                    const o = ls.reduce((n, l) => n + oneTimeOf(l), 0);
                    const recurring = ls.some((l) => l.period !== 'One-time');
                    return (
                      <Fragment key={group}>
                        <tr className="bc-doc-group">
                          <td colSpan={3}>
                            <strong>{group}</strong>
                          </td>
                          <td className="num">{recurring && <strong>{cents(m)}</strong>}</td>
                          <td className="num">{recurring && <strong>{cents(m * 12)}</strong>}</td>
                          {oneTimeColumn && <td className="num">{o ? <strong>{cents(o)}</strong> : ''}</td>}
                        </tr>
                        {ls.map((l) => (
                          <tr key={l.key}>
                            <td>
                              {l.name || '—'}
                              {l.period !== 'Monthly' && l.period !== 'One-time' && <span className="muted"> ({l.period.toLowerCase()})</span>}
                            </td>
                            <td className="num">{formatNumber(l.units)}</td>
                            <td className="num">{cents(l.price)}</td>
                            <td className="num">{l.period === 'One-time' ? '' : cents(monthlyOf(l))}</td>
                            <td className="num">{l.period === 'One-time' ? '' : cents(monthlyOf(l) * 12)}</td>
                            {oneTimeColumn && <td className="num">{l.period === 'One-time' ? cents(oneTimeOf(l)) : ''}</td>}
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </article>
  );
}
